/* eslint-disable max-lines-per-function */
import React from "react";
import { useFormik } from "formik";
import {
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import NumberFormat from "react-number-format";
import { Button, Modal } from "components/haven-design";
import { PropertiesPage_propertiesPage as Properties } from "types/gql-types";
import { unitSizeMetadata } from "model/interest/unit-size";

import {
  SelectAutocomplete,
} from "components/haven-design/form/select";
import { SelectState } from "components/common/form";
import { DialogActions } from "components/haven-design/modal/dialog";
import { initialPropertyFilters } from ".";

export type Filters = {
  searchString: string;
  selectedStates: string[];
  selectedCities: string[];
  unitSizes: string[];
  income: number | null;
  incomeFrequency: "month" | "year";
}

type FiltersModalType = {
  isOpen:boolean;
  onRequestClose:(filters:Filters) => void;
  properties:Properties[];
  currentValues:Filters;
}

const getAvailableCities = (selectedStates:string[], properties:Properties[]):string[] => {
  const stateProperties = selectedStates.length ? properties
    .filter(property => selectedStates.includes(property.state)) : properties;
  return [...new Set(stateProperties.map(property => property.city))].sort();
};

const schema = yup.object({
  income: yup.number().positive().nullable(),
});

const unitSort = (a:string, b:string):number => {
  if (a === "studio") return -1;
  if (b === "studio") return 1;
  if (a > b) return 1;
  if (b > a) return -1;
  return 0;
};

const FiltersModal:React.FC<FiltersModalType> = ({ isOpen,
  onRequestClose,
  properties,
  currentValues }) => {
  const formik = useFormik({
    initialValues: currentValues,
    onSubmit: (values) => onRequestClose(values),
    validationSchema: schema,
  });

  const { values, setFieldValue, setValues } = formik;
  const states = [...new Set(properties.map(property => property.state))].sort();
  const cities = getAvailableCities(values.selectedStates, properties);
  const unitSizes = [...new Set(properties.flatMap(property => property.unitSizes))].sort(unitSort);

  React.useEffect(() => {
    if (isOpen) {
      setValues(currentValues);
    }
  }, [isOpen]);

  return (
    <Modal
      title="Filters"
      fullScreen
      isOpen={isOpen}
      onRequestClose={() => onRequestClose(formik.values)}>
      <DialogContent>
        <form id="filters-form" onSubmit={formik.handleSubmit}>
          <Grid style={{ paddingTop: 16 }} container spacing={1}>
            <Grid size={{ xs: 12 }}><b>Location</b></Grid>
            <Grid size={{ xs: 12 }}>
              <SelectState
                size="medium"
                margin="none"
                multiple
                label="States"
                selected={values.selectedStates}
                value={values.selectedStates}
                includeStates={states}
                onChange={(_event:unknown, selected:string[]) => {
                  setFieldValue("selectedStates", selected);
                  setFieldValue("selectedCities", values.selectedCities.filter(city => getAvailableCities(selected, properties).includes(city)));
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectAutocomplete<string, true>
                multiple
                label="Cities"
                options={cities}
                defaultValue={values.selectedCities}
                value={values.selectedCities}
                onChange={(_event:unknown, selected:string[]) => {
                  setFieldValue("selectedCities", selected);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}><b>Bedrooms</b></Grid>
            <Grid size={{ xs: 12 }}>
              <FormGroup>
                {unitSizes.map((unitSize) =>
                  <FormControlLabel
                    key={unitSize}
                    control={
                      <Checkbox
                        color="primary"
                        onChange={(_e, checked) => {
                          if (checked) {
                            setFieldValue("unitSizes", [...new Set([...values.unitSizes, unitSize])]);
                          } else if (unitSizes.indexOf(unitSize) !== -1) {
                            const result = [...values.unitSizes];
                            result.splice(result.indexOf(unitSize ?? ""), 1);
                            setFieldValue("unitSizes", result);
                          }
                        }}
                        checked={values.unitSizes.includes(unitSize ?? "")}
                        name={`check_${unitSize}`}/>
                    }
                    label={unitSizeMetadata.find(data => data.id.toLowerCase() === unitSize.toLowerCase())?.label ?? ""} />)}
              </FormGroup>
            </Grid>
            <Grid size={{ xs: 12 }}><b>Income</b></Grid>
            <Grid size={{ xs: 7 }}>
              <NumberFormat
                fullWidth
                allowNegative={false}
                variant="outlined"
                label="Income"
                name="income"
                value={values.income}
                customInput={TextField}
                prefix="$"
                type="text"
                thousandSeparator
                onValueChange={({ value }) => formik.setFieldValue("income", value || "")}
                error={Boolean(formik.errors.income)}
                helperText={formik.errors.income}
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="filters-frequency-label" shrink>Frequency</InputLabel>
                <Select
                  labelId="filters-frequency-label"
                  value={values.incomeFrequency}
                  label="Frequency"
                  fullWidth
                  variant="outlined"
                  name="incomeFrequency"
                  onChange={formik.handleChange}>
                  <MenuItem value="month">Per Mo.</MenuItem>
                  <MenuItem value="year">Per Yr.</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button size="L" color="secondary" onClick={(event) => {
          event.preventDefault();
          onRequestClose(initialPropertyFilters);
        }}>Reset All</Button>
        <Button form="filters-form" color="primary" type="submit" size="L">Apply</Button>
      </DialogActions>
    </Modal>);
};

export default FiltersModal;
